import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getAuthHeaders, PATH_INFO } from "../../../../util/helper"
import { Button, Col, Divider, Flex, List, Row } from "antd"
import { CaretRightOutlined, EditOutlined, LeftOutlined, PauseOutlined, ProfileOutlined } from "@ant-design/icons"
import { UpdateUsersContext, UsersContext } from "../../../../../context/userContext"
import { CompanyContext, UpdateCompanyContext } from "../../../../../context/companyContext"
import { DepartmentContext, UpdateDepartmentContext } from "../../../../../context/departmentContext"

export function DepartmentUserDetail() {
  const { user } = useContext(UsersContext)
  const { refreshUser, toggleUserDisabled } = useContext(UpdateUsersContext)
  const { company } = useContext(CompanyContext)
  const { refreshCompany } = useContext(UpdateCompanyContext)
  const { departmentDetail } = useContext(DepartmentContext)
  const { refreshDepartmentDetail } = useContext(UpdateDepartmentContext)
  const { companyId, departmentId, userId } = useParams()
  const navigate = useNavigate()

  const [userState, setUserState] = useState(user.is_disabled)
  const [sampleStats, setSampleStats] = useState<{ clothType: string; count: number }[]>([])
  const [sampleStatsLoading, setSampleStatsLoading] = useState(false)

  useEffect(() => {
    if (!userId || !companyId || !departmentId) return
    refreshUser(userId)
    refreshCompany(companyId)
    refreshDepartmentDetail(departmentId)
  }, [refreshUser, refreshCompany, refreshDepartmentDetail, userId, companyId, departmentId])

  useEffect(() => {
    setUserState(user.is_disabled)
  }, [user])

  const fetchSampleStats = useCallback(async () => {
    if (!userId) return
    setSampleStatsLoading(true)
    try {
      const headers = getAuthHeaders(navigate)
      if (!headers) {
        return
      }
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/${userId}/sample-stats`, {
        headers
      })

      if (res.status === 401) {
        sessionStorage.removeItem("token");
        navigate(PATH_INFO.LOGIN.PATH);
        return;
      }

      if (!res.ok) {
        throw new Error("Failed to fetch sample stats")
      }
      const payload = await res.json()
      setSampleStats(Array.isArray(payload?.items) ? payload.items : [])
    } catch (error) {
      console.error("Error fetching sample stats", error)
      setSampleStats([])
    } finally {
      setSampleStatsLoading(false)
    }
  }, [userId, navigate])

  useEffect(() => {
    fetchSampleStats()
  }, [fetchSampleStats])

  const onClickUserManage = useCallback(() => {
    if (!companyId || !departmentId || !userId) return

    const result = PATH_INFO.DEPARTMENT_USER_MANAGE.PATH.replace(':companyId', companyId)
    const _result = result.replace(':departmentId', departmentId)
    const __result = _result.replace(':userId', userId)

    navigate(__result)
  }, [companyId, departmentId, userId, navigate])

  const dateformat = useCallback((date: string) => {
    if (!date) return
    const result = new Date(date).toISOString().split('T')[0]
    return result
  }, [])

  const userInfoTitle = [
    { key: 'post', label: '役職', value: user.position },
    { key: 'email', label: 'メールアドレス', value: user.email },
    { key: 'pin', label: 'PIN', value: user.pin_code },
    { key: 'end_at', label: '終了予定日', value: company?.contract_end_date },
    { key: 'created_at', label: '登録日', value: dateformat(user.created_at) },
    { key: 'start_at', label: '利用開始日', value: dateformat(user.start_date) },
    { key: 'last_login_at', label: '最終ログイン日', value: dateformat(user.last_login_at) }
  ]

  const onClickToggleStatus = useCallback(async () => {
    if (!userId) return
    try {
      await toggleUserDisabled(userId)
      setUserState((prev) => !prev)
    } catch {
      return
    }
  }, [toggleUserDisabled, userId])

  return (
    <div>
      <Button variant="text" color="primary" icon={<LeftOutlined />} onClick={() => navigate(-1)} className="mb-2 -ml-3">戻る</Button>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">ユーザ詳細画面</p>
        <Button variant="solid" color="primary" icon={<EditOutlined />} onClick={onClickUserManage}>編集</Button>
        <Button variant="outlined" color={userState ? "green" : "danger"} icon={userState ? <CaretRightOutlined /> : <PauseOutlined />} onClick={onClickToggleStatus}>{userState ? '復活' : '停止'}</Button>
      </div>
      <Divider />
      <div className="flex p-2 text-xl mb-3">
        <ProfileOutlined className="text-[#1677ff]" />
        <span className="pl-2 break-all">{`${company?.company_name} / ${departmentDetail.name} / ${user.name}`}</span>
      </div>
      <Flex>
        <Row justify={'start'} align={'top'} className="w-full h-fit" gutter={[24, 24]}>
          {userInfoTitle.map((section, index) => (
            <Col xs={24} xl={12} xxl={8} key={index}>
              <List
                header={<div className="font-bold">{section.label}</div>}
                bordered
                dataSource={[section]}
                renderItem={(item) => (
                  <List.Item style={{ background: '#FFFFFF' }}>
                    <p className={`m-0 pl-2 ${item.value ? '' : 'text-black/25'}`}>{item.value || 'データなし'}</p>
                  </List.Item>)}
                className="overflow-hidden bg-[#FAFAFA]"
              />
            </Col>
          )
          )}
        </Row>
        <div className="w-full max-w-[380px] ml-6" >
          <div className="h-[47px] px-5 py-2 border border-[#d9d9d9] border-solid border-b-0 flex items-center rounded-t-lg bg-[#fafafa] font-semibold" >
            ユーザ採寸型数
          </div>
          <List
            bordered
            loading={sampleStatsLoading}
            dataSource={sampleStats}
            locale={{ emptyText: "データなし" }}
            renderItem={(item) => (
              <List.Item style={{ background: '#FFFFFF' }} >
                <div className="flex w-full justify-between">
                  <span>{item.clothType}</span>
                  <span>{`${item.count}型`}</span>
                </div>
              </List.Item>
            )}
            className="bg-[#FAFAFA] w-full rounded-t-none max-h-[480px] overflow-y-auto"
          />
        </div>
      </Flex>
    </div>
  )
}